import { takePostAuth } from "@/lib/askThemes";
import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";

export default function Auth() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "login" ? "login" : "register");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [forgot, setForgot] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (forgot) {
        await api.post("/auth/forgot-password", { email });
        toast.success("If that email exists, we've sent a reset link.");
        setForgot(false);
        return;
      }
      if (mode === "register") {
        const u = await register(email, password);
        navigate(u.name ? takePostAuth() || "/" : "/setup-name", { replace: true });
      } else {
        const u = await login(email, password);
        navigate(u.name ? takePostAuth() || "/" : "/setup-name", { replace: true });
      }
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="debii-shell flex flex-col px-6 py-10">
      <Link to="/welcome"><Logo size={30} /></Link>

      <div className="flex-1 flex flex-col justify-center max-w-sm w-full mx-auto">
        <h1 className="font-display text-3xl font-extrabold text-foreground">
          {forgot ? "Reset password" : mode === "register" ? "Create your account" : "Welcome back"}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {forgot
            ? "Enter your email and we'll send you a reset link."
            : mode === "register"
            ? "Start keeping track in seconds. Free to get started."
            : "Sign in to see who owes you."}
        </p>

        {!forgot && (
          <div className="mt-6 grid grid-cols-2 gap-1 rounded-xl bg-secondary p-1">
            <button
              data-testid="auth-tab-login"
              onClick={() => setMode("login")}
              className={`h-9 rounded-lg text-sm font-semibold transition-colors ${mode === "login" ? "bg-white text-primary shadow-sm" : "text-muted-foreground"}`}
            >
              Sign In
            </button>
            <button
              data-testid="auth-tab-register"
              onClick={() => setMode("register")}
              className={`h-9 rounded-lg text-sm font-semibold transition-colors ${mode === "register" ? "bg-white text-primary shadow-sm" : "text-muted-foreground"}`}
            >
              Register
            </button>
          </div>
        )}

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <label className="text-sm font-medium text-foreground">Email</label>
            <Input
              data-testid="auth-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="mt-1.5 h-12 rounded-xl"
              autoComplete="email"
            />
          </div>
          {!forgot && (
            <div>
              <label className="text-sm font-medium text-foreground">Password</label>
              <div className="mt-1.5 relative">
                <Input
                  data-testid="auth-password-input"
                  type={show ? "text" : "password"}
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  className="h-12 rounded-xl pr-11"
                  autoComplete={mode === "register" ? "new-password" : "current-password"}
                />
                <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" aria-label="Toggle password visibility">
                  {show ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>
          )}
          <Button data-testid="auth-submit-button" type="submit" disabled={loading} className="w-full h-12 rounded-xl text-base">
            {loading ? "Please wait…" : forgot ? "Send reset link" : mode === "register" ? "Create My Account" : "Sign In"}
          </Button>
        </form>

        <div className="mt-4 text-center">
          {forgot ? (
            <button onClick={() => setForgot(false)} className="text-sm font-medium text-primary">Back to sign in</button>
          ) : mode === "login" ? (
            <button data-testid="auth-forgot-link" onClick={() => setForgot(true)} className="text-sm font-medium text-primary">Forgot password?</button>
          ) : (
            <p className="text-xs text-muted-foreground px-4">
              By creating an account you agree that Debii only stores information you enter. Debii does not lend money or process payments.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
